import { CreditCard, Landmark, Wallet, LayoutDashboard, ArrowLeftRight, Store, GitBranch, Settings } from "lucide-react";

const BARS = [34, 48, 41, 62, 55, 70, 64, 82, 74, 90, 68, 96];

const ROWS = [
  { m: "Kaveri Textiles", method: "Cards", icon: CreditCard, amt: "₹1,84,200", status: "Succeeded" },
  { m: "Nimbus Retail", method: "Bank Transfer", icon: Landmark, amt: "₹96,450", status: "Processing" },
  { m: "Orbit Travels", method: "Wallet", icon: Wallet, amt: "₹2,41,900", status: "Succeeded" },
  { m: "Meridian Stores", method: "Cards", icon: CreditCard, amt: "₹58,320", status: "Refunded" },
];

const STATUS = {
  Succeeded: "bg-emerald-50 text-emerald-600 border-emerald-100",
  Processing: "bg-blue-50 text-blue-600 border-blue-100",
  Refunded: "bg-slate-100 text-slate-500 border-slate-200",
};

const NAV = [
  { icon: LayoutDashboard, label: "Overview", active: true },
  { icon: ArrowLeftRight, label: "Payments" },
  { icon: Store, label: "Merchants" },
  { icon: GitBranch, label: "Routing" },
  { icon: Settings, label: "Settings" },
];

export default function DashboardMock() {
  return (
    <div
      data-testid="zenpay-dashboard-mockup"
      className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_32px_80px_-32px_rgba(11,18,32,0.35)]"
      role="img"
      aria-label="ZenPay merchant dashboard mockup showing payment volume, success rate, active merchants and recent transactions with fictional demo data"
    >
      {/* browser chrome */}
      <div className="flex items-center gap-3 border-b border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#E2E8F0]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#E2E8F0]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#E2E8F0]" />
        </div>
        <div className="font-mono-brand mx-auto flex items-center gap-2 rounded-md border border-[#E2E8F0] bg-white px-3 py-1 text-[11px] text-[#64748B]">
          portal.zenpay.io
        </div>
        <span className="rounded-full border border-[#0EA5A4]/30 bg-[#0EA5A4]/10 px-2.5 py-0.5 text-[10px] font-semibold text-[#0EA5A4]">
          DEMO TENANT
        </span>
      </div>

      <div className="flex">
        {/* sidebar */}
        <div className="hidden w-44 shrink-0 flex-col gap-1 border-r border-[#E2E8F0] bg-[#FBFCFE] p-3 sm:flex">
          <div className="mb-3 flex items-center gap-2 px-2 pt-1">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#1D305B] text-[10px] font-bold text-white">Z</span>
            <span className="text-[13px] font-semibold text-[#1D305B]">ZenPay</span>
          </div>
          {NAV.map((n) => (
            <span
              key={n.label}
              className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[12.5px] font-medium ${
                n.active ? "bg-[#2563EB]/10 text-[#2563EB]" : "text-[#64748B]"
              }`}
            >
              <n.icon className="h-3.5 w-3.5" />
              {n.label}
            </span>
          ))}
        </div>

        {/* main */}
        <div className="min-w-0 flex-1 p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[13px] font-semibold text-[#1D305B]">Good morning, Acme Payments</p>
              <p className="text-[11px] text-[#94A3B8]">Thursday, 9:41 AM IST</p>
            </div>
            <span className="rounded-lg bg-[#1D305B] px-3 py-1.5 text-[11px] font-semibold text-white">+ New Payment Link</span>
          </div>

          {/* stats */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { label: "Volume today", value: "₹48.2L", delta: "+12.4%" },
              { label: "Success rate", value: "98.7%", delta: "+0.6 pts" },
              { label: "Active merchants", value: "1,284", delta: "+18 this week" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-[#E2E8F0] bg-white p-3">
                <p className="text-[10.5px] font-medium text-[#64748B]">{s.label}</p>
                <p className="mt-1 text-[15px] font-bold text-[#1D305B] sm:text-lg">{s.value}</p>
                <p className="mt-0.5 text-[10px] font-medium text-[#0EA5A4]">{s.delta}</p>
              </div>
            ))}
          </div>

          {/* chart */}
          <div className="mt-3 rounded-xl border border-[#E2E8F0] bg-white p-3.5">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold text-[#334155]">Collections by rail</p>
              <div className="flex gap-3 text-[10px] text-[#94A3B8]">
                <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" />Cards</span>
                <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#0EA5A4]" />Bank transfer</span>
              </div>
            </div>
            <div className="mt-3 flex h-20 items-end gap-1.5 sm:h-24">
              {BARS.map((h, i) => (
                <div key={i} className="flex flex-1 flex-col justify-end gap-0.5" style={{ height: "100%" }}>
                  <div className="rounded-sm bg-[#2563EB]/85" style={{ height: `${h}%` }} />
                  <div className="rounded-sm bg-[#0EA5A4]/70" style={{ height: `${Math.max(12, h - 34)}%` }} />
                </div>
              ))}
            </div>
          </div>

          {/* transactions */}
          <div className="mt-3 overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] px-3.5 py-2.5">
              <p className="text-[11px] font-semibold text-[#334155]">Recent transactions</p>
              <p className="text-[10px] font-medium text-[#2563EB]">View all</p>
            </div>
            {ROWS.map((r) => (
              <div key={r.m} className="flex items-center gap-3 border-b border-[#F8FAFC] px-3.5 py-2.5 last:border-0">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] text-[#2563EB]">
                  <r.icon className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-medium text-[#1D305B]">{r.m}</p>
                  <p className="text-[10.5px] text-[#94A3B8]">{r.method}</p>
                </div>
                <p className="font-mono-brand text-[11.5px] font-medium text-[#1D305B]">{r.amt}</p>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS[r.status]}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
